<template>
  <div
    class="tabs"
    :class="{ 'tabs--overflow': moveState.hasOverflow }"
  >
    <div
      ref="scrollRef"
      class="tabs__scroller"
    >
      <div class="tabs__background">
        <template
          v-for="(tab, tabIndex) in $props.tabs"
          :key="tabIndex"
        >
          <div
            class="tab body2"
            :class="{
              'tab--selected': tab.selected,
            }"
            @click="$emit('select', tab.id)"
            v-text="tab.label"
          />
        </template>
      </div>
    </div>

    <basic-popover-menu>
      <template #trigger>
        <div
          class="tabs__toggle"
        >
          <the-icon
            class="tabs__toggle--open"
            art="solid"
            name="chevron-down"
          />

          <the-icon
            class="tabs__toggle--closed"
            art="solid"
            name="chevron-up"
          />
        </div>
      </template>

      <div class="items">
        <button
          v-for="(tab, tabIndex) in $props.tabs"
          :key="tabIndex"
          class="items__item body-2"
          :class="{
            'items__item--selected': tab.selected,
          }"
          @click="$emit('select', tab.id)"
        >
          <span
            class="body-2"
            v-text="tab.label"
          />

          <the-icon
            v-if="tab.selected"
            art="solid"
            name="check"
          />
        </button>
      </div>
    </basic-popover-menu>
  </div>
</template>

<script setup>
  import { nextTick, onMounted, onUnmounted, reactive, useTemplateRef, watch } from 'vue'

  import BasicPopoverMenu from '@shared/components/Basic/PopoverMenu'

  // INIT
  defineEmits(['select'])
  const props = defineProps({
    tabs: {
      type: Array,
      default: () => []
    }
  })

  // DATA
  const scrollRef = useTemplateRef('scrollRef')
  const moveState = reactive({
    hasMoved: false,
    left: 0,
    x: 0,

    fullWidth: 0,
    width: 0
  })

  // METHODS
  function onMouseDown(event) {
    event.preventDefault()

    scrollRef.value.style.cursor = 'grabbing'

    moveState.moved = false

    moveState.left = scrollRef.value.scrollLeft
    moveState.x = event.clientX

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('click', onMouseUp)
    return false
  }

  function onMouseMove(event) {
    event.preventDefault()

    const x = event.clientX - moveState.x
    moveState.hasMoved = true

    scrollRef.value.scrollLeft = moveState.left - x
  }

  function onMouseUp(event) {
    event.preventDefault()

    scrollRef.value.style.cursor = 'grab'
    
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('click', onMouseUp)
  }

  function recalc(_tabs) {
    moveState.fullWidth = scrollRef.value.children[0].scrollWidth
    moveState.width = scrollRef.value.getBoundingClientRect().width

    moveState.hasOverflow = moveState.width < (moveState.fullWidth - 57)
  }

  // LIFECYCLE HOOKS
  onMounted(() => {
    scrollRef.value.addEventListener('mousedown', onMouseDown)
    window.addEventListener('resize', recalc)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', recalc)
  })

  // WATCHER
  watch(() => props.tabs, async () => {
    await nextTick()

    scrollRef.value.querySelector('.tab--selected').scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })

    recalc()
  }, { deep: true, immediate: true })
</script>

<style scoped>
  .tabs {
    --dvp-tabs-height-container: 48px;
    --dvp-tabs-tab-height: 44px;

    background-color: var(--surface-warm);
    border-radius: var(--radius-border-radius-06);
    height: var(--dvp-tabs-height-container);
    padding: var(--fixed-spacing-fix-01) 0 0 var(--fixed-spacing-fix-07);
    width: 100%;
    position: relative;

    .tabs__scroller {
      width: calc(100% - 22px);
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      cursor: grab;

      .tabs__background {
        height: var(--dvp-tabs-tab-height);
        display: flex;
        width: fit-content;
        flex-direction: row;
        background-image: linear-gradient(transparent 0%, var(--surface) 100%);
        align-items: flex-end;
        gap: var(--dvp-tabs-tab-height);
        padding: 0 28px 0 28px;

        .tab {
          border: none;
          position: relative;
          height: var(--dvp-tabs-tab-height);

          padding: 12px var(--fixed-spacing-fix-12);
          background-color: var(--surface-warm);
          border-radius: var(--radius-border-radius-04) var(--radius-border-radius-04) 0 0;
          cursor: inherit;
          user-select: none;
          white-space: nowrap;
          scroll-margin: 50px;

          &:before {
            position: absolute;
            content: '';
            height: var(--dvp-tabs-tab-height);
            width: 28px;
            top: 0;
            left: -28px;
            background-color: var(--surface-warm);
          }

          &:after {
            position: absolute;
            content: '';
            height: var(--dvp-tabs-tab-height);
            width: 28px;
            top: 0;
            right: -28px;
            background-color: var(--surface-warm);
          }

          &--selected {
            font-weight: bold;
            background-color: var(--surface);

            &:before {
              border-radius: 0 0 var(--radius-border-radius-04) 0;
            }

            &:after {
              border-radius: 0 0 0 var(--radius-border-radius-04);
            }
          }

          &:hover {
            background-color: var(--surface);

            &:before {
              border-radius: 0 0 var(--radius-border-radius-04) 0;
            }

            &:after {
              border-radius: 0 0 0 var(--radius-border-radius-04);
            }
          }
        }
      }
    }

    &.tabs--overflow :deep(.popover__trigger) {
      position: absolute;
      height: 50px;
      width: 50px;
      right: 4px;
      top: 2px;
    }

    &.tabs--overflow .tabs__toggle {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &.tabs--overflow :deep(.popover__container) {
      left: 0;
      right: 0;
    }

    &:has(:popover-open) .tabs__toggle--open {
      display: none;
    }

    &:not(:has(:popover-open)) .tabs__toggle--closed {
      display: none;
    }

    .tabs__toggle {
      display: none;
      position: absolute;
      top: 0px;
      right: -2px;
      width: calc(var(--dvp-tabs-height-container) - 2px);
      height: calc(var(--dvp-tabs-height-container) - 2px);
      cursor: pointer;
      border-radius: 50%;
      background-color: var(--Interaction-States-DVP-Tabs-selected);
      border: 4px solid var(--surface-warm);

      &:hover {
        background: var(--surface-ext-b-medium);
      }

      &:focus {
        background: var(--surface-ext-b-medium);
      }

      &:active {
        background: var(--surface-ext-b-high);
      }
    }

    .items {
      display: flex;
      flex-direction: column;

      .items__item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--fixed-spacing-fix-03) var(--fixed-spacing-fix-04);
        color: var(--on-surface);
        cursor: pointer;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
        transition: background 0.1s;

        &--selected {
          background-color: var(--Interaction-States-Dropdowns-Selected);
        }

        &:hover,
        &--selected:hover{
          background-color: var(--Interaction-States-Dropdowns-hovered);
          text-decoration-color: var(--on-surface);
        }
      }
    }
  }
</style>

<style scoped>
  @media (--v-medium) {
    .tabs {
      .items {

        .items__item {
          .icon {
            font-size: 16px;
            width: 20px;
            text-align: center;
          }
        }
      }

      &.tabs--overflow :deep(.popover__container) {
        width: 500px;
        left: initial;
        right: calc(anchor(right) - 250px);
      }
    }
  }
</style>

<style scoped>
  @media (--v-large) {
    .tabs {
      --dvp-tabs-height-container: 60px;
      --dvp-tabs-tab-height: 48px;

      padding: var(--fixed-spacing-fix-03) 0 0 var(--fixed-spacing-fix-07);
    }
  }
</style>

<style scoped>
  @media (--v-ultra) {
    .tabs {
      --dvp-tabs-height-container: 68px;
      --dvp-tabs-tab-height: 56px;
    }
  }
</style>